import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import { Plus, Search, ChevronDown, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from 'src/components/ui/dialog';
import { TestService } from 'src/services/api/TestService';
import {
  useCreateTest,
  useUpdateTest,
  useCreateTestGroup,
  useUpdateTestGroup,
  useCreateTestCollection,
  useUpdateTestCollection,
} from 'src/services/api/mutations/testMutations';
import { useToast } from 'src/hooks/use-toast';
import { cn } from 'src/lib/utils';
import { TestCollectionsTable } from './TableComponents/TestCollectionsTable';
import { TestGroupsTable } from './TableComponents/TestGroupsTable';
import { TestsTable } from './TableComponents/TestTable';
import { NewTest } from './NewTest/NewTest';
import { TableSectionContent } from './TableComponents/TableSectionContent';

const DEFAULT_PAGE_SIZE = 10;

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const TABS = [
  { value: 'tests', label: 'Tests', key: 'tests' },
  { value: 'groups', label: 'Test Groups', key: 'groups' },
  { value: 'collections', label: 'Collections', key: 'collections' },
];

const MultiSelectStatus = ({ selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = (value) => {
    const next = selected.includes(value)
      ? selected.filter((s) => s !== value)
      : [...selected, value];
    if (next.length > 0) onChange(next);
  };

  let label;
  if (selected.length === STATUS_OPTIONS.length) {
    label = 'All Status';
  } else if (selected.length === 0) {
    label = 'Select status';
  } else {
    label = selected.map((s) => s[0] + s.slice(1).toLowerCase()).join(', ');
  }

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex items-center justify-between gap-1.5 px-3 py-2 rounded-lg border bg-background text-[12.5px] font-[500] text-muted-foreground min-w-[150px] transition-all cursor-pointer select-none',
          open
            ? 'border-primary'
            : 'border-input hover:border-primary hover:text-primary'
        )}
      >
        <span className="truncate">{label}</span>
        <ChevronDown
          className={cn(
            'w-3.5 h-3.5 opacity-50 flex-shrink-0 transition-transform duration-150',
            open && 'rotate-180'
          )}
          strokeWidth={1.8}
        />
      </button>
      {open && (
        <div className="absolute right-0 top-[calc(100%+4px)] z-50 bg-background border border-border rounded-[10px] shadow-[0_10px_30px_rgba(0,0,0,0.1),0_1px_4px_rgba(0,0,0,0.06)] p-[5px] min-w-[170px]">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => handleSelect(opt.value)}
              className="flex items-center gap-2 w-full px-3 py-[9px] rounded-[7px] text-[13px] text-foreground-strong cursor-pointer transition-colors hover:bg-brand-bg-subtle text-left"
            >
              <div
                className={cn(
                  'w-4 h-4 rounded flex-shrink-0 border flex items-center justify-center transition-all',
                  selected.includes(opt.value)
                    ? 'bg-primary border-primary'
                    : 'border-input bg-background'
                )}
              >
                {selected.includes(opt.value) && (
                  <Check className="w-2.5 h-2.5 text-white" strokeWidth={2.5} />
                )}
              </div>
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const TestManagementDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('tests');

  const getCurrentType = () => {
    if (activeTab === 'tests') return 'test';
    if (activeTab === 'groups') return 'group';
    return 'collection';
  };

  const getCurrentTypeLabel = () => {
    const type = getCurrentType();
    if (type === 'test') return 'Test';
    if (type === 'group') return 'Group';
    return 'Collection';
  };

  const [tests, setTests] = useState([]);
  const [testGroups, setTestGroups] = useState([]);
  const [testCollections, setTestCollections] = useState([]);

  const [loading, setLoading] = useState({
    tests: false,
    groups: false,
    collections: false,
    submit: false,
  });

  const [pagination, setPagination] = useState({
    tests: { page: 1, total: 0, pageSize: DEFAULT_PAGE_SIZE },
    groups: { page: 1, total: 0, pageSize: DEFAULT_PAGE_SIZE },
    collections: { page: 1, total: 0, pageSize: DEFAULT_PAGE_SIZE },
  });

  const [metrics, setMetrics] = useState({
    tests: 0,
    groups: 0,
    collections: 0,
  });

  const [searchFilters, setSearchFilters] = useState({
    tests: { search: '', status: ['ACTIVE', 'DRAFT'] },
    groups: { search: '', status: ['ACTIVE', 'DRAFT'] },
    collections: { search: '', status: ['ACTIVE', 'DRAFT'] },
  });

  const [localSearch, setLocalSearch] = useState('');
  const searchDebounceRef = useRef(null);

  const [activeModal, setActiveModal] = useState(null);
  const [editingItem, setEditingItem] = useState(null);

  // Create/update go through the mutation hooks; deactivate/publish and the tab
  // list fetches remain direct service calls (no wrapper hooks exist for them).
  const createTest = useCreateTest();
  const updateTest = useUpdateTest();
  const createTestGroup = useCreateTestGroup();
  const updateTestGroup = useUpdateTestGroup();
  const createTestCollection = useCreateTestCollection();
  const updateTestCollection = useUpdateTestCollection();

  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const fetchTests = async () => {
    try {
      setLoading((prev) => ({ ...prev, tests: true }));
      const response = await TestService.listTests(
        pagination.tests.page,
        pagination.tests.pageSize,
        searchFilters.tests.search,
        searchFilters.tests.status
      );
      if (response?.data) {
        setTests(response.data.items || []);
        setPagination((prev) => ({
          ...prev,
          tests: {
            ...prev.tests,
            total: response.data.pagination.total || 0,
            pageSize: response.data.pagination.page_size || 10,
          },
        }));
        if (response.data.metrics) setMetrics(response.data.metrics);
      }
    } catch (error) {
      console.error('Failed to fetch tests:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch tests. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading((prev) => ({ ...prev, tests: false }));
    }
  };

  const fetchTestGroups = async () => {
    try {
      setLoading((prev) => ({ ...prev, groups: true }));
      const response = await TestService.listTestGroups(
        pagination.groups.page,
        pagination.groups.pageSize,
        searchFilters.groups.search,
        searchFilters.groups.status
      );
      if (response?.data) {
        setTestGroups(response?.data?.items || []);
        setPagination((prev) => ({
          ...prev,
          groups: {
            ...prev.groups,
            total: response.data.pagination.total || 0,
            pageSize: response.data.pagination.page_size || 10,
          },
        }));
        if (response.data.metrics) setMetrics(response.data.metrics);
      }
    } catch (error) {
      console.error('Failed to fetch test groups:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch test groups. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading((prev) => ({ ...prev, groups: false }));
    }
  };

  const fetchTestCollections = async () => {
    try {
      setLoading((prev) => ({ ...prev, collections: true }));
      const response = await TestService.listTestCollections(
        pagination.collections.page,
        pagination.collections.pageSize,
        searchFilters.collections.search,
        searchFilters.collections.status
      );
      if (response?.data) {
        setTestCollections(response.data.items || []);
        setPagination((prev) => ({
          ...prev,
          collections: {
            ...prev.collections,
            total: response.data.pagination.total || 0,
            pageSize: response.data.pagination.page_size || 10,
          },
        }));
        if (response.data.metrics) setMetrics(response.data.metrics);
      }
    } catch (error) {
      console.error('Failed to fetch test collections:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch test collections. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading((prev) => ({ ...prev, collections: false }));
    }
  };

  useEffect(() => {
    if (activeTab === 'tests') {
      fetchTests();
    } else if (activeTab === 'groups') {
      fetchTestGroups();
    } else if (activeTab === 'collections') {
      fetchTestCollections();
    }
  }, [
    activeTab,
    pagination.tests.page,
    pagination.tests.pageSize,
    searchFilters.tests,
    pagination.groups.page,
    pagination.groups.pageSize,
    searchFilters.groups,
    pagination.collections.page,
    pagination.collections.pageSize,
    searchFilters.collections,
  ]);

  const handlePageChange = (type, newPage) => {
    setPagination((prev) => ({
      ...prev,
      [type]: { ...prev[type], page: newPage },
    }));
  };

  const handleCreate = (type) => {
    if (type === 'collection') {
      navigate('/tests/collection/new');
    } else if (type === 'group') {
      navigate('/tests/group/new');
    } else {
      setEditingItem(null);
      setActiveModal(type);
    }
  };

  const handleEdit = (type, item) => {
    if (type === 'collection') {
      navigate(`/tests/collection/edit/${item.id}`);
    } else if (type === 'group') {
      navigate(`/tests/group/edit/${item.id}`);
    } else {
      setEditingItem(item);
      setActiveModal(type);
    }
  };

  const handlePreview = (id) => {
    navigate(`/tests/collection/preview/${id}`);
  };

  const handleGroupPreview = (id) => {
    navigate(`/tests/group/preview/${id}`);
  };

  const handleDeactivate = async (type, id) => {
    try {
      setLoading((prev) => ({ ...prev, deactivate: true }));
      if (type === 'test') {
        const response = await TestService.deactivateTest(id);
        if (response?.data) {
          toast({
            title: 'Success',
            description: 'Test deactivated successfully',
          });
          fetchTests();
        }
      } else if (type === 'group') {
        const response = await TestService.deactivateTestGroup(id);
        if (response?.data) {
          toast({
            title: 'Success',
            description: 'Test group deactivated successfully',
          });
          fetchTestGroups();
        }
      } else if (type === 'collection') {
        const response = await TestService.deactivateTestCollection(id);
        if (response?.data) {
          toast({
            title: 'Success',
            description: 'Test collection deactivated successfully',
          });
          fetchTestCollections();
        }
      }
    } catch (error) {
      console.error('Failed to deactivate:', error);
      toast({
        title: 'Error',
        description: 'Failed to deactivate. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading((prev) => ({ ...prev, deactivate: false }));
    }
  };

  const handlePublish = async (type, id, orgId) => {
    try {
      setLoading((prev) => ({ ...prev, publish: true }));
      if (type === 'test') {
        const response = await TestService.publishTest(id);
        if (response?.data) {
          toast({
            title: 'Success',
            description: 'Test published successfully',
          });
          fetchTests();
        }
      } else if (type === 'group') {
        const response = await TestService.publishTestGroup(id);
        if (response?.data) {
          toast({
            title: 'Success',
            description: 'Test group published successfully',
          });
          fetchTestGroups();
        }
      } else {
        const response = await TestService.publishTestCollection(id, orgId);
        if (response?.data) {
          toast({
            title: 'Success',
            description: 'Test collection published successfully',
          });
          fetchTestCollections();
        }
      }
    } catch (error) {
      console.error('Failed to publish:', error);
      toast({
        title: 'Error',
        description: 'Failed to publish. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading((prev) => ({ ...prev, publish: false }));
    }
  };

  const handleSubmit = async (type, formData) => {
    setLoading((prev) => ({ ...prev, submit: true }));
    try {
      let response;
      if (editingItem) {
        switch (type) {
          case 'test':
            response = await updateTest.mutateAsync({
              id: editingItem.id,
              data: formData,
            });
            if (response?.data) {
              setTests((prev) =>
                prev.map((t) => (t.id === editingItem.id ? response.data : t))
              );
              toast({
                title: 'Success',
                description: 'Test updated successfully',
              });
            }
            break;
          case 'group':
            response = await updateTestGroup.mutateAsync({
              id: editingItem.id,
              data: formData,
            });
            if (response?.data) {
              setTestGroups((prev) =>
                prev.map((g) => (g.id === editingItem.id ? response.data : g))
              );
              toast({
                title: 'Success',
                description: 'Test group updated successfully',
              });
            }
            break;
          case 'collection':
            response = await updateTestCollection.mutateAsync({
              id: editingItem.id,
              data: formData,
            });
            if (response?.data) {
              setTestCollections((prev) =>
                prev.map((c) => (c.id === editingItem.id ? response.data : c))
              );
              toast({
                title: 'Success',
                description: 'Test collection updated successfully',
              });
            }
            break;
          default:
            console.warn('Unknown type:', type);
            return;
        }
      } else {
        switch (type) {
          case 'test':
            response = await createTest.mutateAsync(formData);
            if (response?.data) {
              setTests((prev) => [...prev, response.data]);
              toast({
                title: 'Success',
                description: 'Test created successfully',
              });
            }
            break;
          case 'group':
            response = await createTestGroup.mutateAsync(formData);
            if (response?.data) {
              setTestGroups((prev) => [...prev, response.data]);
              toast({
                title: 'Success',
                description: 'Test group created successfully',
              });
            }
            break;
          case 'collection':
            response = await createTestCollection.mutateAsync(formData);
            if (response?.data) {
              setTestCollections((prev) => [...prev, response.data]);
              toast({
                title: 'Success',
                description: 'Test collection created successfully',
              });
            }
            break;
          default:
            console.warn('Unknown type:', type);
            return;
        }
      }
      setActiveModal(null);
      setEditingItem(null);
    } catch (error) {
      console.error('Failed to save changes:', error);
      toast({
        title: 'Error',
        description:
          error.message || 'Failed to save changes. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading((prev) => ({ ...prev, submit: false }));
    }
  };

  const getModalTitle = (type, isEditing) => {
    const action = isEditing ? 'Edit' : 'Create New';
    switch (type) {
      case 'test':
        return `${action} Test`;
      case 'group':
        return `${action} Test Group`;
      case 'collection':
        return `${action} Test Collection`;
      default:
        return '';
    }
  };

  const getModalDescription = (type, isEditing) => {
    if (type === 'test') {
      return isEditing
        ? 'Update the details for this test.'
        : 'Create a new assessment test.';
    }
    return isEditing
      ? 'Update the details for this test group.'
      : 'Create a new group of related tests.';
  };

  const handleStatusChange = (statuses) => {
    setSearchFilters((prev) => ({
      ...prev,
      tests: { ...prev.tests, status: statuses },
      groups: { ...prev.groups, status: statuses },
      collections: { ...prev.collections, status: statuses },
    }));
    setPagination((prev) => ({
      ...prev,
      tests: { ...prev.tests, page: 1 },
      groups: { ...prev.groups, page: 1 },
      collections: { ...prev.collections, page: 1 },
    }));
  };

  const handleSearchChange = (e) => {
    const searchValue = e.target.value;
    setLocalSearch(searchValue);
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchFilters((prev) => ({
        tests: { ...prev.tests, search: searchValue },
        groups: { ...prev.groups, search: searchValue },
        collections: { ...prev.collections, search: searchValue },
      }));
      setPagination((prev) => ({
        tests: { ...prev.tests, page: 1 },
        groups: { ...prev.groups, page: 1 },
        collections: { ...prev.collections, page: 1 },
      }));
    }, 400);
  };

  const renderTestsSection = () => (
    <TableSectionContent
      renderTable={() => (
        <TestsTable
          tests={tests}
          loading={loading.tests}
          onEdit={(id) =>
            handleEdit(
              'test',
              tests.find((t) => t.id === id)
            )
          }
          onDeactivate={(id) => handleDeactivate('test', id)}
          onPublish={(id) => handlePublish('test', id)}
          currentPage={pagination.tests.page}
          pageSize={pagination.tests.pageSize}
          totalItems={pagination.tests.total}
          onPageChange={(page) => handlePageChange('tests', page)}
          onPageSizeChange={(newPageSize) =>
            setPagination((prev) => ({
              ...prev,
              tests: { ...prev.tests, pageSize: Number(newPageSize), page: 1 },
            }))
          }
        />
      )}
    />
  );

  const renderTestGroupsSection = () => (
    <TableSectionContent
      renderTable={() => (
        <TestGroupsTable
          groups={testGroups}
          loading={loading.groups}
          onEdit={(id) =>
            handleEdit(
              'group',
              testGroups?.find((g) => g.id === id)
            )
          }
          onDeactivate={(id) => handleDeactivate('group', id)}
          onPublish={(id) => handlePublish('group', id)}
          onRowClick={(row) => handleGroupPreview(row.id)}
          currentPage={pagination.groups.page}
          totalItems={pagination.groups.total}
          pageSize={pagination.groups.pageSize}
          onPageChange={(page) => handlePageChange('groups', page)}
          onPageSizeChange={(newPageSize) =>
            setPagination((prev) => ({
              ...prev,
              groups: {
                ...prev.groups,
                pageSize: Number(newPageSize),
                page: 1,
              },
            }))
          }
        />
      )}
    />
  );

  const renderTestCollectionsSection = () => (
    <TableSectionContent
      renderTable={() => (
        <TestCollectionsTable
          collections={testCollections}
          loading={loading.collections}
          onEdit={(id) =>
            handleEdit(
              'collection',
              testCollections.find((c) => c.id === id)
            )
          }
          onDeactivate={(id) => handleDeactivate('collection', id)}
          onPublish={(id, orgId) => handlePublish('collection', id, orgId)}
          onRowClick={(row) => {
            if (row.status === 'DRAFT') {
              handleEdit('collection', row);
            } else {
              handlePreview(row.id);
            }
          }}
          currentPage={pagination.collections.page}
          totalItems={pagination.collections.total}
          pageSize={pagination.collections.pageSize}
          onPageChange={(page) => handlePageChange('collections', page)}
          onPageSizeChange={(newPageSize) =>
            setPagination((prev) => ({
              ...prev,
              collections: {
                ...prev.collections,
                pageSize: Number(newPageSize),
                page: 1,
              },
            }))
          }
        />
      )}
    />
  );

  const breadcrumbItems = [
    { label: 'Home', href: '/' },
    { label: 'Test Management', href: '/tests' },
  ];

  return (
    <div>
      <PageHeader
        breadcrumbs={breadcrumbItems}
        actions={
          <button
            type="button"
            onClick={() => handleCreate(getCurrentType())}
            className="inline-flex items-center gap-1.5 px-3.5 py-[7px] rounded-[7px] border border-input bg-card text-[12px] font-[550] text-muted-foreground hover:border-primary hover:text-primary hover:bg-brand-bg-subtle transition-[border-color,color,background-color] cursor-pointer"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={2} />
            Add {getCurrentTypeLabel()}
          </button>
        }
      />

      <PageContainer className="space-y-5">
        <div>
          <p className="text-left text-[13.5px] text-muted-foreground">
            Create and manage assessments, test groups, and collections for the
            hiring pipeline.
          </p>
        </div>

        <div className="flex items-center justify-between gap-4">
          {/* Tabs */}
          <div className="flex items-center border-b border-border">
            {TABS.map((tab) => {
              const count = metrics[tab.key] ?? 0;
              const isActive = activeTab === tab.value;
              return (
                <button
                  key={tab.value}
                  type="button"
                  onClick={() => setActiveTab(tab.value)}
                  className={cn(
                    'px-5 py-2.5 text-[13px] border-b-2 -mb-px bg-transparent cursor-pointer transition-colors flex items-center gap-1.5 whitespace-nowrap',
                    isActive
                      ? 'text-primary font-[600] border-primary'
                      : 'text-muted-foreground font-[500] border-transparent hover:text-foreground-strong'
                  )}
                >
                  {tab.label}
                  <span
                    className={cn(
                      'text-[11px] font-[600] px-[7px] py-[1px] rounded-[10px] leading-[1.5]',
                      isActive
                        ? 'bg-brand-bg-subtle text-primary'
                        : 'bg-muted text-muted-foreground'
                    )}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Search + Status filter */}
          <div className="flex items-center gap-2.5">
            <div className="relative">
              <Search
                className="absolute left-[11px] top-1/2 -translate-y-1/2 h-[15px] w-[15px] text-muted-foreground"
                strokeWidth={2}
              />
              <input
                type="text"
                placeholder="Search…"
                value={localSearch}
                onChange={handleSearchChange}
                className="pl-[34px] pr-3 py-2 rounded-lg border border-input text-[12.5px] text-foreground-strong bg-card placeholder:text-muted-foreground outline-none w-[220px] transition-[border-color,box-shadow] focus:border-primary focus:ring-[3px] focus:ring-primary/[0.08]"
              />
            </div>
            <span className="text-[12px] font-[500] text-muted-foreground whitespace-nowrap">
              Status:
            </span>
            <MultiSelectStatus
              selected={searchFilters.tests.status}
              onChange={handleStatusChange}
            />
          </div>
        </div>

        <div>
          {activeTab === 'tests' && renderTestsSection()}
          {activeTab === 'groups' && renderTestGroupsSection()}
          {activeTab === 'collections' && renderTestCollectionsSection()}
        </div>
      </PageContainer>

      {activeModal === 'test' && (
        <Dialog open onOpenChange={() => setActiveModal(null)}>
          <DialogContent className="sm:max-w-[560px]">
            <DialogHeader>
              <DialogTitle>
                {getModalTitle(activeModal, Boolean(editingItem))}
              </DialogTitle>
              <DialogDescription>
                {getModalDescription(activeModal, Boolean(editingItem))}
              </DialogDescription>
            </DialogHeader>
            <NewTest
              onSubmit={(data) => handleSubmit('test', data)}
              onClose={() => setActiveModal(null)}
              initialData={editingItem}
              loading={loading.submit}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default TestManagementDashboard;
